import { FC, useState, useCallback, useMemo, useEffect } from "react";
import { Box, Popover, Stack, Typography, styled } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { background, divider } from "@/theme/colors";
import { IconCheck } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import {
  TLearningProgressFilterFormExtended,
  defaultLearningProgressFilterValues,
  isEmptyLearningProgressFilterValue,
  normalizeLearningProgressDateFields,
  normalizeLearningProgressLegacyFields,
  stripEmptyLearningProgressFilterFields,
} from "@/features/learning-progress/components/filter/learning-progress-filter-shared";
import { learningProgressFilterSchema } from "@/features/learning-progress/schema/learningProgressFilterSchema";
import { LearningProgressFilterFormFields } from "@/features/learning-progress/components/filter/LearningProgressFilterFormFields";
import { LearningProgressSaveConditionModal } from "@/features/learning-progress/components/filter/LearningProgressSaveConditionModal";
import { useLearningProgressFilterMasterData } from "@/features/learning-progress/components/filter/use-learning-progress-filter-master-data";
import { useCreateSavedCondition } from "@/api/services/main/saved-condition";
import { toast } from "@/utils/toast";
import { TOption } from "@/types/common";

const SAVED_CONDITION_PAGE_TYPE = "learning_progress";

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApplyFilters?: (filters: TLearningProgressFilterFormExtended) => void;
  initialFilters?: Partial<TLearningProgressFilterFormExtended>;
  industryId?: number;
};

export const LearningProgressFilterModal: FC<Props> = ({
  anchorEl,
  onClose,
  onApplyFilters,
  initialFilters,
  industryId,
}) => {
  const open = Boolean(anchorEl);

  const buildDefaults = useCallback(
    (overrides?: Partial<TLearningProgressFilterFormExtended>): TLearningProgressFilterFormExtended => ({
      ...defaultLearningProgressFilterValues,
      ...(normalizeLearningProgressLegacyFields(
        normalizeLearningProgressDateFields(overrides ?? {}) as Partial<Record<string, unknown>>,
      ) as Partial<TLearningProgressFilterFormExtended>),
    }),
    [],
  );

  const methods = useForm<TLearningProgressFilterFormExtended>({
    resolver: yupResolver(learningProgressFilterSchema) as unknown as never,
    defaultValues: buildDefaults(initialFilters),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const filterValues = methods.watch();

  useEffect(() => {
    if (!open) return;
    methods.reset(buildDefaults(initialFilters));
  }, [open, initialFilters, methods, buildDefaults]);

  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const createSavedCondition = useCreateSavedCondition();
  const { options: masterOptions } = useLearningProgressFilterMasterData(industryId);

  const resolvedLabels = useMemo<Partial<Record<keyof TLearningProgressFilterFormExtended, string>>>(() => {
    const labelsFor = (options: TOption[], value: string[]): string | undefined => {
      if (!value || value.length === 0) return undefined;
      const labels = value.map((v) => options.find((o) => String(o.value) === String(v))?.label).filter(Boolean) as string[];
      return labels.length > 0 ? labels.join("、") : undefined;
    };
    const mapping: { key: keyof TLearningProgressFilterFormExtended; options: TOption[] }[] = [
      { key: "rsoCompanyIds", options: masterOptions.organization },
      { key: "nationalityIds", options: masterOptions.nationality },
      { key: "examIds", options: masterOptions.examSchedule },
      { key: "japaneseLanguageLevels", options: masterOptions.japaneseLevel },
      { key: "curriculumProgresses", options: masterOptions.progress },
    ];
    const out: Partial<Record<keyof TLearningProgressFilterFormExtended, string>> = {};
    for (const { key, options } of mapping) {
      const label = labelsFor(options, filterValues[key] as string[]);
      if (label) out[key] = label;
    }
    return out;
  }, [filterValues, masterOptions]);

  const hasFilterData = useMemo(
    () => Object.values(filterValues).some((v) => !isEmptyLearningProgressFilterValue(v)),
    [filterValues],
  );

  const handleSaveCondition = useCallback(
    async (title: string) => {
      const strippedFilters = stripEmptyLearningProgressFilterFields(filterValues);
      await createSavedCondition.mutateAsync({
        pageType: SAVED_CONDITION_PAGE_TYPE,
        title,
        filters: industryId ? { [industryId]: strippedFilters } : strippedFilters,
      });
      toast.success("条件を保存しました。");
    },
    [createSavedCondition, filterValues, industryId],
  );

  const handleClearConfirm = useCallback(() => {
    methods.reset(defaultLearningProgressFilterValues);
    setIsClearConfirmOpen(false);
  }, [methods]);

  const handleApply = useCallback(
    () =>
      methods.handleSubmit((values) => {
        onApplyFilters?.(values);
        onClose();
      })(),
    [methods, onApplyFilters, onClose],
  );

  const handleOpenClearConfirm = useCallback(() => {
    if (hasFilterData) setIsClearConfirmOpen(true);
  }, [hasFilterData]);

  const handleCloseClearConfirm = useCallback(() => setIsClearConfirmOpen(false), []);
  const handleCloseSaveModal = useCallback(() => setIsSaveModalOpen(false), []);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{
        "& .MuiPaper-root": {
          mt: 1,
          p: 0,
          borderRadius: "8px",
          boxShadow: "0px 8px 24px 0px #00000026",
        },
      }}
    >
      <PopoverContainer>
        <FormProvider {...methods}>
          <ContentContainer>
            <PanelHeader>
              <Typography variant="sub16Bold">フィルタ</Typography>
            </PanelHeader>

            <ScrollableContent>
              <LearningProgressFilterFormFields industryId={industryId} />
            </ScrollableContent>
          </ContentContainer>

          <FooterContainer>
            <Stack flexDirection="row" gap="16px">
              <Button variant="ghost" onClick={onClose} size="sm">
                キャンセル
              </Button>
              <Button variant="text" onClick={handleOpenClearConfirm} size="sm" disabled={!hasFilterData}>
                クリア
              </Button>
            </Stack>
            <Stack flexDirection="row" gap="16px">
              {/* 一時的に「設定条件を保存」ボタンを非表示にしている。
              <Button variant="outline" size="sm" startIcon={<IconSave />} onClick={() => setIsSaveModalOpen(true)} disabled={!hasFilterData}>
                設定条件を保存
              </Button>
              */}
              <Button onClick={handleApply} size="sm" startIcon={<IconCheck width={20} height={20} />}>
                条件を設定
              </Button>
            </Stack>
          </FooterContainer>
        </FormProvider>
      </PopoverContainer>

      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={handleCloseClearConfirm}
        onOk={handleClearConfirm}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />

      <LearningProgressSaveConditionModal
        isOpen={isSaveModalOpen}
        onClose={handleCloseSaveModal}
        onSave={handleSaveCondition}
        filterValues={filterValues}
        resolvedLabels={resolvedLabels}
      />
    </Popover>
  );
};

const PopoverContainer = styled(Box)`
  width: 780px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background: ${background.white};
  border-radius: 8px;
`;

const ContentContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const PanelHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid ${divider.middle};
`;

const ScrollableContent = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 24px 24px;
`;

const FooterContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid ${divider.middle};
`;
